import React, { useState, useRef, useEffect } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { useTts } from 'tts-react'
import type { TTSHookState } from 'tts-react'
import { v4 as uuidv4 } from 'uuid';
import { dtcListState, messagesState, sessionIdState, showReviewState, vehicleState, userHasReviewedState } from './states';
import { Message } from './types';
import { MdKeyboardVoice, MdVolumeUp, MdStopCircle, MdContentCopy, MdDone, MdOutlineThumbUp, MdThumbUp, MdOutlineThumbDown, MdThumbDown } from "react-icons/md";
import Review from './Review';
import sendIcon from './assets/send-icon.png';
import sendIconDisabled from './assets/send-icon-disabled.png';
import redDotIcon from './assets/bot-chat-icon.png';
import loadingIcon from './assets/loading-icon.png';
import './ChatWindow.css';
import ChatSuggestion from './ChatSuggestion';
import { getAnswer, thumbsUp, thumbsDown, getSynthesize} from './services/ChatService';
import AcceptTerms from './AcceptTerms';
import { Disclosure } from './Disclosure';
import Container from 'react-bootstrap/esm/Container';
import { sessionTitleState } from './states';

const sessionId = uuidv4();

const questionSuggestions = [
    "How do I jump start my car?",
    "Which side is my gas cap on?",
    "What type of gas does my car need?",
    "How do I replace my car key battery?",
    "When do I need to service my car?",
    "What tire pressure do my tires need?",
    "My battery is dead.",
    "How do I fill the washer fluid?",
    "How do I check my shocks?",
]

const ChatBotLoadingSpinner = () => {
    return (
        <div className="spinner-container">
            <img src={loadingIcon} alt="Loading..." className="whirling-icon" />
        </div>
    );
};

const MessageComponent = ({ children, msg }: { children: React.ReactNode, msg: Message }) => {
    const [messages, setMessages] = useRecoilState(messagesState);
    const [done, setDone] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null); // Reference to the Audio instance

    const { ttsChildren, state, play, stop } = useTts({
        children,
        markTextAsSpoken: true,
        lang: 'en-US',
        voice: window.speechSynthesis.getVoices().find(voice => voice.lang === 'en-US' && voice.voiceURI === 'Google US English') || undefined,
    })

    const handleSpeak = (state: TTSHookState) => {
        state.isPlaying ? stop() : play();
    }

    const handleAudioEnded = () => {
        setIsPlaying(false);
    };

    const handleSynthesize = async () => {
        try {
            if (isPlaying) {
                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }
                setIsPlaying(false);
            } else {
                if (!audioRef.current) {
                    const audioData = await getSynthesize(msg.text);
                    audioRef.current  = new Audio(URL.createObjectURL(audioData.data));
                }
                audioRef.current.play();
                audioRef.current.onended = handleAudioEnded;
                setIsPlaying(true);
            }
        } catch (error) {
          console.error('Error:', error);
        }
    };

    const handleCopy = (children: string) => {
        navigator.clipboard.writeText(children);
        setDone(true);
    }

    const callThumbs = async (id: string, action: string) => {
        if (action === 'up') {
            let isAdded = !msg.like;
            thumbsUp(id, sessionId, isAdded)
        } else if (action === 'down') {
            let isAdded = !msg.dislike;
            thumbsDown(id, sessionId, isAdded)
        }
    }

    const handleLike = (id: string) => {
        setMessages(messages?.map((msg, i) => msg.id === id ? { ...msg, like: !msg.like } : msg));
        callThumbs(id, 'up')
    }

    const handleDisLike = (id: string) => {
        setMessages(messages?.map((msg, i) => msg.id === id ? { ...msg, dislike: !msg.dislike } : msg));
        callThumbs(id, 'down')
    }


    return (
        <>
            {ttsChildren}
            <div className="message-button">
                <span>
                    <button type="button" className={isPlaying ? `action-button done` : `action-button`} onClick={() => handleSynthesize()} >
                        {isPlaying ? <MdStopCircle /> : <MdVolumeUp />}
                    </button>
                    {/* {audioSrc && <audio controls src={audioSrc}></audio>} */}
                </span>
                <span>
                    <button type="button" className={done ? `action-button done` : `action-button`} onClick={() => handleCopy(msg.text)}>
                        {done ? <MdDone /> : <MdContentCopy />}
                    </button>
                </span>
                {msg.id != null ? (
                    <>
                        <span>
                            <button type="button" className={msg.like ? `action-button done` : `action-button`} onClick={() => handleLike(msg.id)} disabled={msg.dislike}>
                                {msg.like ? <MdThumbUp /> : <MdOutlineThumbUp />}
                            </button>
                        </span>
                        <span>
                            <button type="button" className={msg.dislike ? `action-button done` : `action-button`} onClick={() => handleDisLike(msg.id)} disabled={msg.like}>
                                {msg.dislike ? <MdThumbDown /> : <MdOutlineThumbDown />}
                            </button>
                        </span>
                    </>
                ) : null}

            </div>
        </>
    )
};

const ChatWindow = () => {
    const setSessionId = useSetRecoilState(sessionIdState);
    const vehicle = useRecoilValue(vehicleState);
    const dtcs = useRecoilValue(dtcListState);
    const [messages, setMessages] = useRecoilState(messagesState);
    const userHasReviewed = useRecoilValue(userHasReviewedState);
    const setShowReview = useSetRecoilState(showReviewState);
    const [sessionTitle, setSessionTitle] = useRecoilState(sessionTitleState);

    const [input, setInput] = useState("");
    const [lastUserMessage, setLastUserMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showSuggestion, setShowSuggestion] = useState(false);
    const [showStartChatButton, setShowStartChatButton] = useState(true);
    const [suggestionStrings, setSuggstionStrings] = useState<string[]>([]);
    const [showAcceptTerms, setShowAcceptTerms] = useState(true);

    const disableChat = loading || !vehicle.make || !vehicle.model || !vehicle.year;

    const chatDivRef = useRef<HTMLDivElement>(null);
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition,
        finalTranscript,
    } = useSpeechRecognition();

    useEffect(() => {
        let hasAcceptedTermKey = "hasAcceptedTermKey";
        let hasAcceptedTerms = localStorage.getItem(hasAcceptedTermKey);
        if (hasAcceptedTerms?.toLowerCase() === "accepted") {
            setShowAcceptTerms(false);
            handleStartChat();
        }

        let sessionKey = "sessionKey";
        let localSessionId = localStorage.getItem(sessionKey);
        if (localSessionId && localSessionId?.length > 0) {
            setSessionId(localSessionId);
        } else {
            setSessionId(sessionId);
            localStorage.setItem(sessionKey, sessionId)
        }
    }, [])

    useEffect(() => {
        chatDivRef.current?.scrollTo({ top: chatDivRef.current.scrollHeight, behavior: "smooth" });
    }, [loading, showSuggestion, messages]);

    useEffect(() => {
        setInput(transcript);
    }, [transcript]);

    useEffect(() => {
        if (input.trim() === "")
            return;
        // eslint-disable-next-line
    }, [finalTranscript]);

    const handleProceed = (e: React.MouseEvent<HTMLButtonElement>) => {
        setShowAcceptTerms(false);
        e.preventDefault();
        handleStartChat();
    };

    const handleStartChat = () => {
        setMessages((messages) => [...messages, { id: messages.length.toString(), text: 'Hi there, you can ask me any question related to your vehicle. Here are a few examples below:', user: 'init' }]);
        setShowSuggestion(true);
        setSuggstionStrings(getRandomStrings());
        setShowStartChatButton(false);
    }

    const sendMessage = (event: React.FormEvent<HTMLButtonElement | HTMLFormElement>) => {
        event.preventDefault();
        if (input.trim() === "" || !vehicle.make || !vehicle.model || !vehicle.year || loading) return;
        setShowSuggestion(false);
        setMessages((messages) => [...messages, { id: messages.length.toString(), text: input, user: 'user' }]);
        setLastUserMessage(input);
        getBotResponse(input);
        setInput("");
        resetTranscript();
    };

    const getBotResponse = async (input: string) => {
        setLoading(true);
        const botResponse = await getAnswer(input, vehicle, dtcs, sessionId, sessionTitle);
        setLoading(false);
        setMessages((prevMessages) => [
            ...prevMessages,
            { id: botResponse.data.messageId, text: botResponse.data.answer, user: 'bot' },
        ]);

        if (messages?.filter(msg => msg.user === 'user').length >= 5 && !userHasReviewed) {
            setShowReview(true);
        }
    };

    const handleSuggestion = (e: React.MouseEvent<HTMLDivElement>, text: string) => {
        e.preventDefault();
        setShowSuggestion(false);
        setMessages((messages) => [...messages, { id: messages.length.toString(), text: text, user: 'user' }]);
        getBotResponse(text);
    }

    const handleVoiceInput = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (listening) {
            SpeechRecognition.stopListening();
        } else {
            SpeechRecognition.startListening();
        }
    }

    function getRandomStrings(): string[] {
        if (questionSuggestions.length < 3) {
            throw new Error("The array must contain at least 3 strings.");
        }

        const result: string[] = [];
        const usedIndices: Set<number> = new Set();

        while (result.length < 3) {
            const randomIndex = Math.floor(Math.random() * questionSuggestions.length);

            if (!usedIndices.has(randomIndex)) {
                usedIndices.add(randomIndex);
                result.push(questionSuggestions[randomIndex]);
            }
        }

        return result;
    }


    return (
        <>
            {showAcceptTerms && <AcceptTerms onProceed={handleProceed} />}
            {!showAcceptTerms && (
                <>
                    <Container  fluid={true}>
                        <div className="chat-window">
                            <div className="chat-messages" ref={chatDivRef}>
                                {messages?.map((msg, index) => (
                                    <div key={msg.id} className={`chat-message ${msg.user}`}>
                                        <div className="message-header">
                                            {msg.user !== 'user' && <img src={redDotIcon} alt="" className="red-dot" />}
                                            <span className="message-title">{msg.user === 'user' ? 'You' : 'Diagnostic AI'}</span>
                                        </div>
                                        <div className="message-box">
                                            {msg.user === 'bot' ?
                                                <MessageComponent msg={msg} >
                                                    <p>{msg.text}</p>
                                                </MessageComponent>
                                                :
                                                <p>{msg.text}</p>
                                            }
                                        </div>
                                    </div>
                                ))}
                                {loading && (
                                    <div className={`chat-message bot`}>
                                        <div className="message-header">
                                            <img src={redDotIcon} alt="" className="red-dot" />
                                            <span className="message-title">Diagnostic AI</span>
                                        </div>
                                        <ChatBotLoadingSpinner />
                                    </div>
                                )}
                                {showSuggestion &&
                                    <>
                                        {suggestionStrings?.map((suggestion, index) => {
                                            return (
                                                <ChatSuggestion 
                                                    key={index}
                                                    handleSuggestion={handleSuggestion}
                                                    suggestionString={suggestion} 
                                                    disabled={!vehicle.make || !vehicle.model || !vehicle.year} />)
                                        })}
                                    </>
                                }
                            </div>
                            <form className={`chat-input ${disableChat ? 'disable' : ''}`} onSubmit={sendMessage}>
                                <input
                                    className="send-text"
                                    type="text"
                                    value={input}
                                    disabled={disableChat}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={listening ? "Listening" : loading ? "" : "Hello there..."}
                                />
                                <div className='send-button-container'>
                                    {browserSupportsSpeechRecognition ? 
                                    (<button type="button" className={`send-button ${loading ? 'disable' : ''}`} disabled={disableChat} onClick={handleVoiceInput}>
                                        <div className={listening ? "pulse" : ""} />
                                        <MdKeyboardVoice style={{ width: 24, height: 24, color: disableChat ? '#CCC' : 'DA291C' }} />
                                    </button>) : 
                                    (null)}
                                    <button type="button" className={`send-button ${loading ? 'disable' : ''}`} disabled={disableChat} onClick={sendMessage}>
                                        {disableChat ? 
                                            (<img src={sendIconDisabled} alt="Send Icon" className="send-icon" />) : 
                                            (<img src={sendIcon} alt="Send Icon" className="send-icon" />)}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Container>
                    {/* <Review title="Review" /> */}
                    <Disclosure />
                </>
            )}
        </>
    )
}

export default ChatWindow;